import type { IntersectingDocOption } from "building-types/options/intersectingOptionSet";
import type { IntersectionConditions, ModifiableField } from "building-types/options/intersectionBaseTypes";
import { modifiableFields } from "building-types/options/intersectionBaseTypes";
import type { OptionChoices } from "building-types/options/optionChoices";
import type { OptionDefinitions } from "building-types/options/optionDefinitions";
import type { OptionPrices } from "building-types/options/optionPricing";
import type { IntersectingOptionSet, DocOptionSet, DocOption } from "building-types/options/optionSet";
import type { Multimap } from "common/data/multimap";
import type { Table } from "common/data/table";
import { assertUnreachable } from "common/utils/assertUnreachable";
import type { ActiveOptions } from "../activeOptions";
import { getOptionSetArchetypeOrSelf } from "../getOptionSetArchetypeOrSelf";
import { getOptionChoicesWithDefaults, isIntersectingOptionSet } from "../optionChoicesTools";
import { optionPriceWithIntersections } from "./intersectingOptionSetPrice";
import {
  type ActiveIntersectionEffect,
  intersectionModifiesField,
  type SegmentedIntersectionEffects,
} from "./intersectingOptionSetShared";

export function intersectingOptionSetNewDefault(optionSet: DocOptionSet): IntersectingOptionSet {
  return {
    ...optionSet,
    defaultValue: "",
    options: {},
    optionSetType: "INTERSECTING",
    scope: {},
    formatting: {},
    effect: {
      type: "GENERATE",
    },
    conditions: {},
  };
}

export function isModifyIntersection(optionSet: IntersectingOptionSet) {
  return optionSet.effect.type === "MODIFY";
}

export function isGenerateIntersection(optionSet: IntersectingOptionSet) {
  return optionSet.effect.type === "GENERATE";
}

/**
 * Option-set IDs whose displayed options a linked option can change — the MODIFY and GENERATE
 * targets of every intersecting option set. A set that isn't in here is never overridden by an
 * intersection, so callers can skip intersection-aware preprocessing for its rows.
 */
export function getIntersectionTargetedOptionSetIds(optionSets: Table<DocOptionSet>): Set<string> {
  const targetedIds = new Set<string>();
  for (const optionSet of Object.values(optionSets)) {
    if (!isIntersectingOptionSet(optionSet)) {
      continue;
    }
    const target = isModifyIntersection(optionSet)
      ? optionSet.effect.modifySettings?.optionSetId
      : optionSet.effect.generateSettings?.targetOptionSetId;
    if (target) {
      targetedIds.add(target);
    }
  }
  return targetedIds;
}

interface IndexedIntersectingOption {
  readonly option: DocOption;
  readonly conditions: IntersectionConditions;
  readonly requiredChoiceCount: number;
}

interface IntersectingOptionIndex {
  readonly [optionSetId: string]: {
    readonly [optionId: string]: readonly number[];
  };
}

export interface CompiledIntersectingOptionSet {
  readonly options: readonly IndexedIntersectingOption[];
  readonly choiceIndex: IntersectingOptionIndex;
}

interface CachedCompiledIntersectingOptionSet {
  readonly options: IntersectingOptionSet["options"];
  readonly conditions: IntersectingOptionSet["conditions"];
  readonly compiled: CompiledIntersectingOptionSet;
}

// Doc option sets are updated immutably in normal Studio/service flows. If an intersecting set's
// options or conditions are mutated in place, callers must replace those table references or this
// cache can keep the previous condition index.
const compiledIntersectingOptionSets = new WeakMap<IntersectingOptionSet, CachedCompiledIntersectingOptionSet>();

export function compileIntersectingOptionSet(optionSet: IntersectingOptionSet): CompiledIntersectingOptionSet {
  const cached = compiledIntersectingOptionSets.get(optionSet);
  if (cached?.options === optionSet.options && cached.conditions === optionSet.conditions) {
    return cached.compiled;
  }

  const options: IndexedIntersectingOption[] = [];
  const choiceIndex: { [optionSetId: string]: { [optionId: string]: number[] } } = {};

  for (const option of Object.values(optionSet.options)) {
    const optionIndex = options.length;
    const conditions = optionSet.conditions[option.id];
    options.push({
      option,
      conditions,
      requiredChoiceCount: Object.keys(conditions?.choices ?? {}).length,
    });

    for (const [conditionOptionSetId, optionIds] of Object.entries(conditions?.choices ?? {})) {
      const indexedOptionIds = new Set<string>();
      for (const optionId of optionIds) {
        if (indexedOptionIds.has(optionId)) {
          continue;
        }
        indexedOptionIds.add(optionId);

        if (!choiceIndex[conditionOptionSetId]) {
          choiceIndex[conditionOptionSetId] = {};
        }
        if (!choiceIndex[conditionOptionSetId][optionId]) {
          choiceIndex[conditionOptionSetId][optionId] = [];
        }
        choiceIndex[conditionOptionSetId][optionId].push(optionIndex);
      }
    }
  }

  const compiled = { options, choiceIndex };
  compiledIntersectingOptionSets.set(optionSet, {
    options: optionSet.options,
    conditions: optionSet.conditions,
    compiled,
  });
  return compiled;
}

// For each active choice, increment the satisfied-condition count on every combo row that lists it.
// Returned array is sparse, keyed by compiled row index.
export function computeIntersectingMatchCounts(
  compiled: CompiledIntersectingOptionSet,
  optionChoices: OptionChoices,
): number[] {
  const matchCounts: number[] = [];

  for (const [optionSetId, optionChoice] of Object.entries(optionChoices)) {
    if (!optionChoice) {
      continue;
    }

    const candidateIndexes = compiled.choiceIndex[optionSetId]?.[optionChoice];
    if (!candidateIndexes) {
      continue;
    }

    for (const candidateIndex of candidateIndexes) {
      matchCounts[candidateIndex] = (matchCounts[candidateIndex] ?? 0) + 1;
    }
  }

  return matchCounts;
}

// A combo row matches when its satisfied-condition count equals requiredChoiceCount.
// Scans rows in compiled doc order; if several rows match, the earliest wins (legacy tie-break).
export function findIntersectingMatchFromCounts(
  compiled: CompiledIntersectingOptionSet,
  matchCounts: readonly number[],
): IntersectingDocOption | undefined {
  for (let index = 0; index < compiled.options.length; index++) {
    const indexedOption = compiled.options[index];
    if ((matchCounts[index] ?? 0) === indexedOption.requiredChoiceCount) {
      return { option: indexedOption.option, conditions: indexedOption.conditions };
    }
  }
  return undefined;
}

export function matchIntersectingOption(
  optionSet: IntersectingOptionSet,
  buildingOptionChoices: OptionChoices,
): IntersectingDocOption | undefined {
  const compiled = compileIntersectingOptionSet(optionSet);
  const matchCounts = computeIntersectingMatchCounts(compiled, buildingOptionChoices);
  return findIntersectingMatchFromCounts(compiled, matchCounts);
}

export function calculateActiveIntersectionEffects(
  optionChoices: OptionChoices,
  definitions: OptionDefinitions,
): SegmentedIntersectionEffects {
  optionChoices = getOptionChoicesWithDefaults(definitions, optionChoices);
  const modify: { [optionSetId: string]: ActiveIntersectionEffect[] } = {};
  const generate: ActiveIntersectionEffect[] = [];

  for (const optionSet of Object.values(definitions.optionSets)) {
    if (!isIntersectingOptionSet(optionSet)) {
      continue;
    }

    const match = matchIntersectingOption(optionSet, optionChoices);
    if (!match) {
      continue;
    }

    const effect: ActiveIntersectionEffect = { ...match, effect: optionSet.effect };
    if (isGenerateIntersection(optionSet)) {
      generate.push(effect);
    } else if (isModifyIntersection(optionSet) && optionSet.effect.modifySettings?.optionSetId) {
      const targetOptionSetId = optionSet.effect.modifySettings.optionSetId;
      if (!modify[targetOptionSetId]) {
        modify[targetOptionSetId] = [];
      }
      modify[targetOptionSetId].push(effect);
    }
  }

  return { modify, generate };
}

export function pickModifyingIntersectionValue<K extends keyof DocOption>(
  option: DocOption | null | undefined,
  intersections: ReadonlyArray<ActiveIntersectionEffect>,
  key: K,
): DocOption[K] | undefined {
  const modifyingIntersections = intersections?.filter(intersectionModifiesField(key));
  if (modifyingIntersections?.length) {
    // How should we handle clashing overrides
    return modifyingIntersections[0].option[key];
  }
  return option?.[key];
}

type ModificationEntry = {
  [K in ModifiableField]: [K, DocOption[K] | undefined];
}[ModifiableField];

function nonPriceModifications(
  option: DocOption | null | undefined,
  modifications: ReadonlyArray<ActiveIntersectionEffect>,
): ModificationEntry[] {
  const entries: ModificationEntry[] = [];
  for (const field of modifiableFields) {
    switch (field) {
      case "price":
        break;
      case "builderOptionCode":
      case "description":
      case "salesImages":
        entries.push([field, pickModifyingIntersectionValue(option, modifications, field)] as ModificationEntry);
        break;
      default:
        assertUnreachable(field);
    }
  }
  return entries;
}

export function getOptionModifications(
  option: DocOption,
  modifications: ReadonlyArray<ActiveIntersectionEffect>,
  choicesWithDefaults?: OptionChoices,
): Partial<DocOption> {
  const entries = nonPriceModifications(option, modifications);
  if (modifiableFields.includes("price")) {
    // Pass choices through so a formula-priced base option evaluates against the active choices
    // rather than falling back to its MatchExpression default.
    entries.push(["price", optionPriceWithIntersections(option, modifications, choicesWithDefaults)]);
  }
  return Object.fromEntries(entries);
}

export function buildActiveOptions(
  definitions: OptionDefinitions,
  optionPrices: OptionPrices,
  modify: Multimap<ActiveIntersectionEffect>,
  withModifyingIntersections: boolean,
): ActiveOptions {
  return Object.fromEntries(
    Object.entries(optionPrices)
      .map(([optionSetId, { optionId, price }]) => {
        const optionSet = getOptionSetArchetypeOrSelf(definitions, optionSetId);
        if (!optionSet) {
          return null;
        }
        const option = optionId !== null ? optionSet.options[optionId] : undefined;
        // Output key is optionSetId (may be instance ID) to support per-instance state.
        // Lookup modifications by archetype ID since that's how modify is keyed.
        return [
          optionSetId,
          {
            ...option,
            ...(withModifyingIntersections
              ? Object.fromEntries(nonPriceModifications(option, modify[optionSet.id]))
              : {}),
            price,
            salesCategory: optionSet.salesCategory,
          },
        ];
      })
      .filter((entry): entry is [string, ActiveOptions[string]] => entry !== null),
  );
}
